import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { getSubscriptionGate } from '../utils/subscription';

const SubscriptionContext = createContext({});

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}

export function SubscriptionProvider({ children }) {
  const { userDoc, user } = useAuth();
  const [subscriptionData, setSubscriptionData] = useState({
    isActive: false,
    isTrialActive: false,
    daysLeft: 0,
    planType: 'none',
    studentLimit: 5,
    studentsUsed: 0,
    trialEnded: false
  });

  useEffect(() => {
    if (!userDoc) {
      setSubscriptionData({
        isActive: false,
        isTrialActive: false,
        daysLeft: 0,
        planType: 'none',
        studentLimit: 5,
        studentsUsed: 0,
        trialEnded: false
      });
      return;
    }

    const gate = getSubscriptionGate(userDoc);
    const studentsUsed = userDoc.studentsCreated || 0;

    // Check if subscription is active (paid)
    const isSubscriptionActive = gate.reason === 'paid';

    // Check trial status using the gate
    const isTrialActive = gate.reason === 'trial_active';
    const trialEnded = gate.reason === 'trial_expired';

    // Calculate days left if trial is active
    let daysLeft = 0;
    if (isTrialActive && gate.trialEndsAtMs) {
      const msLeft = gate.trialEndsAtMs - Date.now();
      daysLeft = Math.max(0, Math.ceil(msLeft / (1000 * 60 * 60 * 24)));
    }
    
    // Determine student limit based on plan
    let studentLimit = userDoc.studentsLimit || 5; // Use Firestore value or default
    if (isSubscriptionActive) {
      const plan = userDoc.plan || 'none';
      switch (plan) {
        case 'monthly_basic':
        case 'yearly_basic':
          studentLimit = 50;
          break;
        default:
          studentLimit = 50;
      }
    }

    setSubscriptionData({
      isActive: isSubscriptionActive,
      isTrialActive,
      daysLeft,
      planType: userDoc.plan || 'none',
      studentLimit,
      studentsUsed,
      trialEnded,
      hasAccessToFeatures: gate.allowed
    });
  }, [userDoc]);

  const checkFeatureAccess = (feature = 'basic') => {
    const { isActive, isTrialActive, trialEnded } = subscriptionData;

    // Check if this is a tester account - bypass all restrictions
    if (userDoc?.isTesterAccount === true) {
      return { allowed: true, reason: 'tester_account', message: 'Tester account - full access' };
    }

    // If trial ended and no active subscription, deny access
    if (trialEnded && !isActive) {
      return {
        allowed: false,
        reason: 'trial_ended',
        message: 'Your trial has ended. Please subscribe to continue using ExamLock.'
      };
    }

    // If no trial and no subscription, deny access to premium features
    if (!isTrialActive && !isActive) {
      return {
        allowed: false,
        reason: 'no_subscription',
        message: 'This feature requires an active subscription or trial.'
      };
    }

    return { allowed: true, reason: null, message: null };
  };

  const checkStudentLimit = () => {
    const { studentLimit, studentsUsed } = subscriptionData;

    // Check if this is a tester account - unlimited students
    if (userDoc?.isTesterAccount === true) {
      return {
        canAddMore: true,
        limit: 9999,
        used: studentsUsed,
        remaining: 9999
      };
    }

    const canAddMore = studentsUsed < studentLimit;
    const remainingSlots = Math.max(0, studentLimit - studentsUsed);

    return {
      canAddMore,
      limit: studentLimit,
      used: studentsUsed,
      remaining: remainingSlots
    };
  };

  const value = {
    ...subscriptionData,
    checkFeatureAccess,
    checkStudentLimit
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}